// ─── Asset Loader ─────────────────────────────────────────────────────────────
//
// Bridges two surfaces:
//   • On-disk manifest at /assets/surggies/manifest.json — flat key → URL map
//     used by sprite-sheet.ts and the renderer.
//   • A backward-compatible nested category shape (tilesheets / characters /
//     buildings / props), which some older tools may still emit.
//
// The manifest on disk is the source of truth. This loader accepts either the
// nested category structure or a pre-flattened Record<string,string>, then
// normalizes it into the flat-key cache consumed by the engine.
//
// AssetLoader.loadAll() flattens the manifest into prefixed keys and registers
// each successfully-loaded image with the SpriteSheet instance under the flat
// key — sprite-sheet's own async loadSprite() cache uses the same key space, so
// either lookup path resolves to the same asset.

import { SpriteSheet, registerSprite } from "./sprite-sheet";

export interface AssetManifest {
  tilesheets: Record<string, string>;
  characters: Record<string, string>;
  buildings: Record<string, string>;
  props: Record<string, string>;
}

/** Flat key -> URL map (matches sprite-sheet.ts internal manifest shape). */
export type FlatAssetManifest = Record<string, string>;

/** Public URL of the on-disk nested manifest. */
const MANIFEST_URL = "/assets/surggies/manifest.json";

/** Category -> flat-key prefix. Mirrors sprite-sheet.ts CATEGORY_PREFIX. */
const CATEGORY_PREFIX: Record<keyof AssetManifest, string> = {
  tilesheets: "tile_",
  characters: "char_",
  buildings: "bldg_",
  props: "prop_",
};

// Default empty manifest — kept for backwards compatibility with callers that
// pass nothing and want placeholder rendering only.
const DEFAULT_MANIFEST: AssetManifest = {
  tilesheets: {},
  characters: {},
  buildings: {},
  props: {},
};

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof Image !== "undefined";
}

/**
 * Flatten the nested manifest into the prefixed-key shape that sprite-sheet.ts
 * and renderer.ts consume. Unknown / malformed groups are skipped silently.
 */
export function flattenAssetManifest(
  manifest: Partial<AssetManifest> | FlatAssetManifest,
): FlatAssetManifest {
  const out: FlatAssetManifest = {};

  // Already-flat manifest: keep keys as-is.
  const flatEntries = Object.entries(manifest).filter(([, v]) => typeof v === "string");
  if (flatEntries.length > 0 && !("tilesheets" in manifest || "characters" in manifest || "buildings" in manifest || "props" in manifest)) {
    for (const [k, v] of flatEntries) out[k] = v as string;
    return out;
  }

  // Nested category manifest: flatten with prefixes.
  for (const [category, prefix] of Object.entries(CATEGORY_PREFIX) as Array<
    [keyof AssetManifest, string]
  >) {
    const group = (manifest as Partial<AssetManifest>)[category];
    if (!group || typeof group !== "object") continue;
    for (const [k, v] of Object.entries(group)) {
      if (typeof v === "string") out[`${prefix}${k}`] = v;
    }
  }
  return out;
}

/**
 * Fetch the on-disk nested manifest. SSR-safe (returns empty manifest on the
 * server). Failures degrade to an empty manifest so the engine falls back to
 * procedural placeholders.
 */
export async function fetchAssetManifest(): Promise<AssetManifest> {
  if (!isBrowser()) return { ...DEFAULT_MANIFEST };
  try {
    const res = await fetch(MANIFEST_URL, { cache: "force-cache" });
    if (!res.ok) {
      console.warn(
        `[asset-loader] manifest fetch returned ${res.status}; using empty manifest`,
      );
      return { ...DEFAULT_MANIFEST };
    }
    const json = (await res.json()) as Partial<AssetManifest>;
    return {
      tilesheets: { ...(json.tilesheets ?? {}) },
      characters: { ...(json.characters ?? {}) },
      buildings: { ...(json.buildings ?? {}) },
      props: { ...(json.props ?? {}) },
    };
  } catch (err) {
    console.warn("[asset-loader] manifest load failed:", err);
    return { ...DEFAULT_MANIFEST };
  }
}

export class AssetLoader {
  private spriteSheet: SpriteSheet;
  private loaded: boolean = false;
  private loadPromise: Promise<void> | null = null;
  private flatManifest: FlatAssetManifest = {};

  constructor(spriteSheet: SpriteSheet) {
    this.spriteSheet = spriteSheet;
  }

  /**
   * Load an individual image and return a promise.
   */
  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
      img.src = src;
    });
  }

  /**
   * Load all assets. When no manifest is passed, fetches the on-disk nested
   * manifest from /assets/surggies/manifest.json. Images that fail to load are
   * silently skipped — the engine uses color placeholders when sprites are
   * missing.
   */
  async loadAll(manifest?: AssetManifest): Promise<void> {
    if (this.loaded) return;
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = this._doLoad(manifest);
    await this.loadPromise;
    this.loaded = true;
  }

  private async _doLoad(manifest?: AssetManifest): Promise<void> {
    const resolved = manifest ?? (await fetchAssetManifest());
    const flat = flattenAssetManifest(resolved);
    this.flatManifest = flat;

    const entries = Object.entries(flat);
    if (entries.length === 0) return;

    // Load all images concurrently, skip failures
    const results = await Promise.allSettled(
      entries.map(async ([key, src]) => {
        const img = await this.loadImage(src);
        return { key, img };
      }),
    );

    for (const result of results) {
      if (result.status === "fulfilled") {
        this.spriteSheet.addSheet(result.value.key, result.value.img);
        // Mirror into sprite-sheet's module cache so getCachedSprite()
        // consumers (tiles, props, autotile edges) see manifest assets too.
        registerSprite(result.value.key, result.value.img);
      }
    }
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  getSpriteSheet(): SpriteSheet {
    return this.spriteSheet;
  }

  /**
   * Flat-key accessor. Returns the cached image registered on the underlying
   * SpriteSheet under the prefixed key (e.g. "tile_grass", "char_ceo_1"), or
   * null if not loaded.
   */
  getSprite(key: string): HTMLImageElement | OffscreenCanvas | null {
    return this.spriteSheet.getSheet(key) ?? null;
  }

  /** Whether a given flat key has been registered on the SpriteSheet. */
  hasSprite(key: string): boolean {
    return this.spriteSheet.hasSheet(key);
  }

  /** All flat keys this loader knows about from the most recent manifest. */
  getManifestKeys(): string[] {
    return Object.keys(this.flatManifest);
  }

  /** The flattened key -> URL map from the most recent manifest. */
  getFlatManifest(): FlatAssetManifest {
    return { ...this.flatManifest };
  }
}
